/**
 * Local helpers for the three coach event screens.
 *
 * Nothing here re-implements a rule that lives in lib/api.ts or in SQL. The
 * schedule grouping below is built on top of splitSchedule() rather than
 * beside it: it only separates cases the coach experiences differently
 * ("Michel asked you" is not the same as "nobody asked, but you may
 * volunteer"), and it never decides what a coach is allowed to see.
 *
 * Copy rule: no em dashes in anything a coach reads, plain hyphens only.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { splitSchedule, type CoachEventCard } from '../../lib/api'
import { DUBAI_TZ, formatDay, pluralize, toDate } from '../../lib/format'

/* -------------------------------------------------------------------------- */
/* Time                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A clock that ticks, so a countdown is right without a refresh. Coaches leave
 * this screen open on the way to a job, so "starts in 40 min" has to keep
 * counting down while they look at it.
 */
export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

const dubaiDayFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: DUBAI_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** The Asia/Dubai calendar day of a timestamp, as YYYY-MM-DD. */
export function dubaiDayKey(value: string | Date): string {
  return dubaiDayFmt.format(toDate(value))
}

/** "Today", "Tomorrow", "Yesterday", or "Tue, 8 Sep". Always Dubai days. */
export function dayLabel(value: string | Date, now: Date = new Date()): string {
  const key = dubaiDayKey(value)
  if (key === dubaiDayKey(now)) return 'Today'
  if (key === dubaiDayKey(new Date(now.getTime() + 86_400_000))) return 'Tomorrow'
  if (key === dubaiDayKey(new Date(now.getTime() - 86_400_000))) return 'Yesterday'
  return formatDay(value)
}

const dubaiHourFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: DUBAI_TZ,
  hour: '2-digit',
  hourCycle: 'h23',
})

/** "Good morning" and friends, on Dubai's clock rather than the device's. */
export function greeting(now: Date = new Date()): string {
  const hour = Number(dubaiHourFmt.format(now))
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

export type EventPhase = 'upcoming' | 'running' | 'finished'

export function eventPhase(
  startsAt: string,
  endsAt: string,
  now: Date = new Date(),
): EventPhase {
  const t = now.getTime()
  if (t >= new Date(endsAt).getTime()) return 'finished'
  if (t >= new Date(startsAt).getTime()) return 'running'
  return 'upcoming'
}

/**
 * The countdown line. Deliberately coarser the further away it is: nobody needs
 * minutes for something three weeks out, and everybody wants them an hour
 * before.
 */
export function countdownLabel(
  startsAt: string,
  endsAt: string,
  now: Date = new Date(),
): string {
  const phase = eventPhase(startsAt, endsAt, now)
  if (phase === 'finished') return 'Finished'
  if (phase === 'running') return 'Happening now'

  const minutes = Math.max(1, Math.round((new Date(startsAt).getTime() - now.getTime()) / 60_000))
  if (minutes < 60) return `Starts in ${minutes} min`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const rest = minutes % 60
    const h = `${hours} ${pluralize(hours, 'hour')}`
    return rest === 0 ? `Starts in ${h}` : `Starts in ${h} ${rest} min`
  }

  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  const d = `${days} ${pluralize(days, 'day')}`
  return restHours === 0
    ? `Starts in ${d}`
    : `Starts in ${d} ${restHours} ${pluralize(restHours, 'hour')}`
}

/* -------------------------------------------------------------------------- */
/* Demo live updates                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Shared with the admin, materials and finance screens. The hook lives in
 * lib/useLiveReload.ts; it is re-exported here so the coach screens keep
 * importing it from their own helpers.
 */
export { useLiveReload } from '../../lib/useLiveReload'

/* -------------------------------------------------------------------------- */
/* Actions                                                                    */
/* -------------------------------------------------------------------------- */

export interface ActionState {
  busy: boolean
  /** Message from a refused write. Every api.ts error is written to be shown. */
  error: string | null
  /** Short confirmation, cleared after a few seconds. */
  done: string | null
  run: (fn: () => Promise<unknown>, doneMessage?: string) => Promise<boolean>
  clear: () => void
}

/**
 * One write, with the three things a screen always needs afterwards: a disabled
 * button while it runs, the refusal message if the backend says no, and a short
 * confirmation if it said yes. Every function in lib/api.ts throws an Error
 * whose message is written to be read by a coach, so it is shown verbatim.
 */
export function useAction(onDone?: () => void): ActionState {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const alive = useRef(true)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      window.clearTimeout(timer.current)
    }
  }, [])

  const run = useCallback(
    async (fn: () => Promise<unknown>, doneMessage?: string) => {
      setBusy(true)
      setError(null)
      setDone(null)
      try {
        await fn()
        if (alive.current) {
          if (doneMessage) {
            setDone(doneMessage)
            window.clearTimeout(timer.current)
            timer.current = window.setTimeout(() => {
              if (alive.current) setDone(null)
            }, 5_000)
          }
          setBusy(false)
        }
        onDone?.()
        return true
      } catch (e) {
        if (alive.current) {
          setError(e instanceof Error ? e.message : String(e))
          setBusy(false)
        }
        return false
      }
    },
    [onDone],
  )

  const clear = useCallback(() => {
    setError(null)
    setDone(null)
  }, [])

  return { busy, error, done, run, clear }
}

/* -------------------------------------------------------------------------- */
/* Schedule grouping                                                          */
/* -------------------------------------------------------------------------- */

export interface CoachSchedule {
  /** Assigned, started, not finished. The coach is probably standing there. */
  inProgress: CoachEventCard[]
  /** Michel asked, no answer yet. */
  needsAnswer: CoachEventCard[]
  /** Qualified for the service, nobody asked. The volunteering path. */
  openToYou: CoachEventCard[]
  /** Assigned, not confirmed yet. */
  needsConfirm: CoachEventCard[]
  /** Assigned and confirmed. */
  booked: CoachEventCard[]
  /** Answered, waiting on Michel to decide. */
  waitingOnMichel: CoachEventCard[]
  upcoming: CoachEventCard[]
  past: CoachEventCard[]
  cancelled: CoachEventCard[]
  /** The next event that is actually theirs, else the next one at all. */
  next: CoachEventCard | null
}

/**
 * Split a coach's schedule into the piles they think in. splitSchedule() in
 * lib/api.ts owns the upcoming / past / needs-answer rules; this only takes its
 * needsAnswer pile apart, because "Michel asked you" and "you could offer" read
 * very differently to the person holding the phone.
 */
export function coachSchedule(cards: CoachEventCard[], now: Date = new Date()): CoachSchedule {
  const { upcoming, past, cancelled, needsAnswer, needsConfirm } = splitSchedule(cards, now)

  const inProgress = cards.filter(
    (c) =>
      c.assignment !== null &&
      c.event.status !== 'cancelled' &&
      eventPhase(c.event.starts_at, c.event.ends_at, now) === 'running',
  )
  const running = new Set(inProgress.map((c) => c.event.id))

  return {
    inProgress,
    needsAnswer: needsAnswer.filter((c) => c.availability !== null),
    openToYou: needsAnswer.filter((c) => c.availability === null),
    needsConfirm,
    booked: upcoming.filter((c) => c.assignment !== null && c.assignment.status !== 'assigned'),
    waitingOnMichel: upcoming.filter(
      (c) =>
        c.assignment === null &&
        c.availability !== null &&
        c.availability.response !== 'pending',
    ),
    upcoming,
    past: past.filter((c) => !running.has(c.event.id)),
    cancelled,
    next: upcoming.find((c) => c.assignment !== null) ?? upcoming[0] ?? null,
  }
}
